<template>
    <form @submit.prevent="submit()" :class="{ 'is-loading' : loading }" role="form">
        <!-- Login input-->
        <div class="field">
            <div class="control has-icons-left is-expanded">
                <input v-model.trim="login"
                       v-validate="'required|min:3|max:50|unique-login'"
                       data-vv-name="login"
                       data-vv-validate-on="input"
                       data-vv-delay="300"
                       :placeholder="'components.common.quick-login-reg.registration.login' | translate"
                       class="input is-small"
                       maxlength="50"
                       autofocus
                       autocomplete="login"
                       type="text" />
                <span class="icon is-small is-left">
                    <i class="fas fa-user"></i>
                </span>
            </div>
            <p class="help is-danger" v-if="errors.has('login:required')">{{"components.common.quick-login-reg.registration.validation.login required" | translate}}</p>
            <p class="help is-danger" v-if="errors.has('login:min')">{{"components.common.quick-login-reg.registration.validation.login min length" | translate}}</p>
            <p class="help is-danger" v-if="errors.has('login:max')">{{"components.common.quick-login-reg.registration.validation.login max length" | translate}}</p>
            <p class="help is-danger" v-if="errors.has('login:unique-login')">{{"components.common.quick-login-reg.registration.validation.login in use" | translate}}</p>
        </div>
        <!-- Password input-->
        <div class="field">
            <div class="control has-icons-left has-icons-right">
                <input v-model.trim="password"
                       v-validate="{required:true, min:4, max:20, regex:/^[^\s]+$/}"
                       data-vv-name="password"
                       data-vv-validate-on="input"
                       :placeholder="'components.common.quick-login-reg.registration.password' | translate"
                       class="input is-small"
                       :type="showPassword ? 'text' : 'password'"
                       maxlength="20"
                       ref="password" />
                <span class="icon is-small is-left"><i class="fas fa-lock"></i></span>
                <a class="icon is-small is-right is-clickable" @click="toggleShowPassword('password')">
                    <span v-show="!showPassword"><i class="far fa-eye-slash"></i></span>
                    <span v-show="showPassword"><i class="far fa-eye"></i></span>
                </a>
                <p class="help is-danger" v-if="errors.has('password:required')">{{"components.common.quick-login-reg.registration.validation.password required" | translate}}</p>
                <p class="help is-danger" v-if="errors.has('password:min')">{{"components.common.quick-login-reg.registration.validation.password min length" | translate}}</p>
                <p class="help is-danger" v-if="errors.has('password:max')">{{"components.common.quick-login-reg.registration.validation.password max length" | translate}}</p>
            </div>
        </div>
        <!-- Alias input-->
        <div v-if="askAliasDuringRegistration" class="field">
            <div class="control has-icons-left">
                <input v-model.trim="alias"
                       v-validate="'min:3|max:50'"
                       data-vv-name="alias"
                       data-vv-validate-on="input"
                       :placeholder="'components.common.quick-login-reg.registration.alias' | translate"
                       class="input is-small"
                       type="text"
                       maxlength="30" />
                <span class="icon is-small is-left"><i class="fas fa-user"></i></span>
                <p class="help is-danger" v-if="errors.has('alias:min')">{{"components.common.quick-login-reg.registration.validation.alias min length" | translate}}</p>
                <p class="help is-danger" v-if="errors.has('alias:max')">{{"components.common.quick-login-reg.registration.validation.alias max length" | translate}}</p>
            </div>
        </div>
        <!-- Recaptcha-->
        <div v-if="false" class="has-text-centered">
            <recaptcha ref="recaptcha" :on-response="response => { recaptchaResponse = response; }" />
        </div>
        <!-- Currency input-->
        <div v-if="showCurrencies" class="field center-field">
            <div class="control has-icons-left">
                <div class="select is-small">
                    <select v-model="currency" v-validate="'required'" data-vv-name="currency" data-vv-validate-on="change">
                        <option :value="null">
                            {{ 'components.common.quick-login-reg.registration.choose currency' | translate }}
                        </option>
                        <option v-for="(c, index) of visibleCurrencies" :key="index" :value="c.id">
                            {{ c.description }} ({{ c.id }})
                        </option>
                    </select>
                </div>
                <span class="icon is-left"><i class="fas fa-money-bill-alt"></i></span>
                <p class="help is-danger" v-if="errors.has('currency:required')">{{"components.common.quick-login-reg.registration.validation.currency required" | translate}}</p>
            </div>
        </div>
        <!-- Agreement input-->
        <div class="field">
            <div class="control">
                <label class="checkbox">
                    <input v-model="agreement" v-validate="'required'" data-vv-name="agreement" data-vv-validate-on="change" type="checkbox" />
                    <span class="is-size-7">{{'components.pages.registration.agreement' | translate}}</span>
                    <router-link :to="{ name: 'rules-inner', params: { rule: mainRulesRef && mainRulesRef.name } }" class="is-size-7">{{'components.pages.registration.agreement rules' | translate}}</router-link>
                </label>
                <p class="help is-danger" v-if="errors.has('agreement:required')">{{"components.pages.registration.validation.agreement required" | translate}}</p>
            </div>
        </div>
        <!-- Submit-->
        <button :class="[{ 'is-loading': loading }, 'card-footer-item button is-warning is-small is-fullwidth']">
            <span v-if="!loading">{{"components.common.quick-login-reg.registration.register" | translate}}</span>
            <span class="icon" v-if="loading"><i class="fas fa-circle-notch fa-pulse"></i></span>
        </button>
    </form>
</template>

<script>
    import Vue from 'vue';
    import { mapGetters } from 'vuex';

    import registrationService from '../../../../../_common/services/registration.service';
    import currencyHelper from '../../../../../_common/helpers/currency.helper';
    import rulesHelper from '../../../../../_common/helpers/rules.helper';

    import Recaptcha from '../recaptcha/recaptcha.vue';

    import UniqueLoginValidationRule from '../../../../../_common/mixins/unique-login.validation-rule.mixin';

    export default {
        name: 'quick-login-reg-registration-by-login',
        components: { Recaptcha },
        mixins: [UniqueLoginValidationRule],
        $_veeValidate: { validator: 'new' },
        data() {
            return {
                showPassword: false,
                loading: false,
                login: '',
                password: '',
                alias: null,
                currency: null,
                recaptchaResponse: null,
                agreement: false,
                mainRulesRef: null,
                visibleCurrencies: null
            };
        },
        computed: {
            ...mapGetters(['client']),
            askAliasDuringRegistration() {
                return this.client && this.client.view.askAliasDuringRegistration;
            },
            showCurrencies() {
                return this.client && this.client.createDefaultBalance;
            },
            registrationBanner() {
                return this.client && this.client.view.registrationBanner;
            },
            lang() {
                return Vue.state.lang.currentLang;
            }
        },
        async created() {
            this.visibleCurrencies = await currencyHelper.getVisibleCurrencies();
            if (this.visibleCurrencies && this.visibleCurrencies.length) {
                this.currency = this.visibleCurrencies[0].id;
            }

            this.mainRulesRef = await rulesHelper.getMainRulesRef(this.lang);
        },
        methods: {
            toggleShowPassword(ref) {
                this.showPassword = !this.showPassword;
                setTimeout(() => this.$refs[ref].focus());
            },
            async submit() {
                await this.$validator.validateAll();
                if (this.errors.any() || this.loading) return;

                this.errors.clear();
                this.loading = true;
                let registrationRequest = {
                    username: this.login,
                    password: this.password,
                    language: Vue.state.lang.currentLang,
                    currency: this.currency,
                    alias: this.alias,
                    recaptchaResponse: this.recaptchaResponse
                };
                let result = await registrationService.register(registrationRequest);
                await Vue.state.player.reload();
                await Vue.router.teleport.afterRegistration();
                this.loading = false;
            }
        }
    };
</script>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// front/src/skins/bulmasite/components/common/quick-login-reg/registration-by-login.vue